import { PALETTE, GLOW } from "../design-tools/tokens";

/* ─────────────────────────────────────────────
   LEGAL PAGE SHARED LAYOUT BITS
───────────────────────────────────────────── */
function LegalNav() {
  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "22px 56px",
        background: "rgba(13,15,20,0.92)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: `1px solid ${PALETTE.border}`,
      }}>
      <a
        href="/"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          textDecoration: "none",
        }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: "15px",
            letterSpacing: "0.06em",
            color: PALETTE.white,
            lineHeight: 1,
          }}>
          Digital Billions Group
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "8px",
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            color: PALETTE.teal500,
          }}>
          Global Digital Innovation
        </span>
      </a>
      <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
        <a href="/terms" className="nav-link">
          Terms
        </a>
        <a href="/privacy" className="nav-link">
          Privacy
        </a>
        <a href="/#contact" className="nav-link">
          Contact
        </a>
      </div>
    </nav>
  );
}

function LegalFooter() {
  return (
    <div
      style={{
        borderTop: `1px solid ${PALETTE.border}`,
        padding: "24px 56px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
        background: "linear-gradient(180deg, transparent, rgba(13,15,20,0.6))",
      }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: PALETTE.teal300,
        }}>
        ◈ DIGITAL BILLIONS GROUP &nbsp; ◌ &nbsp; MMXXII—MMXXVI
      </span>
      <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
        <a href="/" className="nav-link footer-link">
          Home
        </a>
        <a href="/terms" className="nav-link footer-link">
          Terms
        </a>
        <a
          href="mailto:hello@digitalbillionsgroup.com"
          className="nav-link footer-link">
          Contact
        </a>
      </div>
    </div>
  );
}

function Article({ number, title, children }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 400,
          fontSize: "clamp(20px, 2.4vw, 28px)",
          lineHeight: 1.2,
          color: PALETTE.white,
          marginBottom: 18,
          display: "flex",
          gap: 14,
          alignItems: "baseline",
        }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
            letterSpacing: "0.1em",
            color: PALETTE.teal500,
          }}>
          {number}
        </span>
        {title}
      </h2>
      <div
        style={{
          fontFamily: "var(--font-body)",
          fontWeight: 300,
          fontSize: "15.5px",
          lineHeight: 1.85,
          color: PALETTE.whiteMid,
        }}>
        {children}
      </div>
    </section>
  );
}

function Placeholder({ children }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: "0.92em",
        color: PALETTE.teal400,
        background: "rgba(127,161,194,0.1)",
        border: `1px dashed ${PALETTE.borderBright}`,
        padding: "1px 7px",
        borderRadius: 2,
      }}
      title="Placeholder — confirm with counsel / business before publishing">
      {children}
    </span>
  );
}

function Callout({ label, children }) {
  return (
    <div
      style={{
        border: `1px solid ${PALETTE.borderBright}`,
        background: "rgba(127,161,194,0.05)",
        padding: "18px 20px",
        marginBottom: 20,
      }}>
      <p
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "10px",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: PALETTE.teal400,
          marginBottom: 10,
        }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function TwoColTable({ headLeft, headRight, rows }) {
  return (
    <div style={{ border: `1px solid ${PALETTE.border}`, marginBottom: 8 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          padding: "10px 16px",
          background: "rgba(127,161,194,0.06)",
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: PALETTE.teal400,
          fontFamily: "var(--font-mono)",
        }}>
        <span>{headLeft}</span>
        <span>{headRight}</span>
      </div>
      {rows.map(([a, b], i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            padding: "12px 16px",
            borderTop: `1px solid ${PALETTE.border}`,
            fontSize: "13.5px",
            color: PALETTE.whiteMid,
          }}>
          <span>{a}</span>
          <span>{b}</span>
        </div>
      ))}
    </div>
  );
}

function DataTable({ headers, rows }) {
  return (
    <div style={{ border: `1px solid ${PALETTE.border}`, marginBottom: 8 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${headers.length}, 1fr)`,
          gap: 16,
          padding: "10px 16px",
          background: "rgba(127,161,194,0.06)",
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: PALETTE.teal400,
          fontFamily: "var(--font-mono)",
        }}>
        {headers.map((h) => (
          <span key={h}>{h}</span>
        ))}
      </div>
      {rows.map((cells, i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${headers.length}, 1fr)`,
            gap: 16,
            padding: "12px 16px",
            borderTop: `1px solid ${PALETTE.border}`,
            fontSize: "13.5px",
            color: PALETTE.whiteMid,
          }}>
          {cells.map((c, j) => (
            <span key={j}>{c}</span>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PRIVACY POLICY
───────────────────────────────────────────── */
export default function PrivacyPolicy() {
  return (
    <div style={{ background: PALETTE.ink, minHeight: "100vh" }}>
      <LegalNav />

      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "160px 24px 100px",
        }}>
        <p
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: PALETTE.teal400,
            marginBottom: 24,
          }}>
          Platform & Services
        </p>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontSize: "clamp(38px, 6vw, 64px)",
            lineHeight: 1.02,
            letterSpacing: "-0.02em",
            color: PALETTE.white,
            marginBottom: 28,
            textShadow: GLOW.text,
          }}>
          Privacy Policy
        </h1>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 300,
            fontSize: "17px",
            lineHeight: 1.8,
            color: PALETTE.whiteMid,
            marginBottom: 32,
          }}>
          How Digital Billions Group handles the data you share with us, and our
          position on the ownership and use of that data.
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 24,
            borderTop: `1px solid ${PALETTE.border}`,
            borderBottom: `1px solid ${PALETTE.border}`,
            padding: "16px 0",
            marginBottom: 48,
            fontFamily: "var(--font-mono)",
            fontSize: "11px",
            letterSpacing: "0.08em",
            color: PALETTE.teal300,
          }}>
          <span>
            <strong style={{ color: PALETTE.teal100 }}>LAST UPDATED</strong>{" "}
            June 24, 2026
          </span>
          <span>
            <strong style={{ color: PALETTE.teal100 }}>PROVIDER</strong> Digital
            Billions Group LLC
          </span>
          <span>
            <strong style={{ color: PALETTE.teal100 }}>APPLIES TO</strong>{" "}
            Clients, Users &amp; Visitors
          </span>
        </div>

        <p
          style={{
            fontFamily: "var(--font-body)",
            fontStyle: "italic",
            fontSize: "15px",
            lineHeight: 1.8,
            color: PALETTE.whiteSoft,
            marginBottom: 56,
          }}>
          This Privacy Policy is incorporated by reference into the Platform
          Terms of Service. Where a signed agreement (MSA, technology-transfer
          agreement, DPA, or BAA) governs specific data, that agreement controls
          for that data.
        </p>

        <Article number="1" title="Who We Are & What This Covers">
          <p>
            Digital Billions Group LLC ("DBG," "we," "us") provides software,
            platforms, and managed and professional services. This Policy
            explains what data we collect, how we use it, our position on the
            ownership of data you provide, and the choices available to you. It
            applies to our websites, applications, dashboards, and Services.
          </p>
        </Article>

        <Article number="2" title="The Data We Handle">
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              <strong style={{ color: PALETTE.white }}>Account data</strong> —
              Name, business contact details, role, credentials, and
              authentication metadata.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>Billing data</strong> —
              Plan, invoices, payment status, and limited payment metadata. Card
              details are handled by our payment processor; we do not store full
              card numbers.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Usage & telemetry
              </strong>{" "}
              — Log data, device and browser information, feature usage,
              performance metrics, and diagnostic data.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Communications & recordings
              </strong>{" "}
              — Messages, support tickets, and recordings or transcripts of
              calls, meetings, demonstrations, and presentations conducted with
              us.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>Client Content</strong> —
              Data, files, records, and materials you upload or generate through
              the Services.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Non-HIPAA inquiry data
              </strong>{" "}
              — Website visitor data, marketing inquiries, discovery-form
              submissions, and general communications that do not involve PHI
              — see Section 8.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Regulated data (where applicable)
              </strong>{" "}
              — PHI or other regulated data, only where a BAA or DPA is in place
              and only as that agreement permits — see Section 8.
            </li>
          </ul>
        </Article>

        <Article number="3" title="How We Use Data">
          <p>
            We use data to operate, secure, support, and improve the Services;
            to authenticate users and process billing; to communicate with you;
            to provide and document our work; to maintain records of
            communications and recordings as described in the Terms; to detect
            and prevent fraud, abuse, and security incidents; and to meet legal
            obligations.
          </p>
        </Article>

        <Article number="4" title="Data Ownership — Our Position">
          <Callout label="Your Data Is Yours. We Do Not Claim Ownership of It.">
            <p>
              Client Content belongs to you. DBG does not claim, assert, or
              imply ownership of Client Content, your customers' data, or any
              other data you submit through the Services, under any
              circumstance and regardless of how that data is used to provide
              the Services. Where a signed MSA, SOW, technology-transfer
              agreement, DPA, or BAA states that specified data belongs to
              you, that agreement controls, and we process that data only as
              a service provider acting on your instructions.
            </p>
          </Callout>
          <p style={{ marginBottom: 16 }}>
            <strong style={{ color: PALETTE.white }}>
              4.1 Reserved operating license.
            </strong>{" "}
            Except where a signed agreement provides otherwise, you grant DBG a
            non-exclusive, worldwide, royalty-free license to host, store,
            process, analyze, and use the data and materials you provide as
            necessary to deliver, secure, maintain, support, and improve the
            Services, and to operate our business in connection with the
            Services. This license exists so we can run the Services; it does
            not change your ownership of your data, and it does not grant DBG
            any right to use your data for purposes unrelated to providing the
            Services to you.
          </p>
          <p>
            <strong style={{ color: PALETTE.white }}>
              4.2 Aggregated & de-identified data.
            </strong>{" "}
            We may create and use aggregated, anonymized, statistical, and
            de-identified data and service metadata — which do not identify you,
            your customers, or any individual — for analytics, benchmarking,
            security, product development, and improvement of our Services. We
            may retain and use such data without time limit. We do not
            re-identify de-identified data. This Section never applies to PHI
            or other data governed by a BAA or DPA except as that agreement
            separately permits.
          </p>
        </Article>

        <Article number="5" title="Our Role: Processor, Service Provider & Business Associate">
          <p style={{ marginBottom: 16 }}>
            For enterprise and regulated clients, DBG's role with respect to
            your data depends on the governing agreement in place. We do not
            act as an independent controller, owner, or unrestricted user of
            your data; we act in one or more of the following capacities:
          </p>
          <DataTable
            headers={["Role", "When It Applies", "Governing Document"]}
            rows={[
              [
                "Processor / Service Provider",
                "General commercial engagements involving personal data subject to state privacy law (e.g., CCPA/CPRA) or similar frameworks.",
                "DPA, MSA, or these Terms",
              ],
              [
                "Business Associate",
                "Engagements involving PHI on behalf of a HIPAA Covered Entity or another Business Associate.",
                "Business Associate Agreement (BAA)",
              ],
              [
                "Hosting / Infrastructure Provider",
                "Managed-service and subscription engagements with no regulated-data processing.",
                "Order / Platform Terms of Service",
              ],
            ]}
          />
          <p style={{ marginTop: 16 }}>
            In each capacity, we process data only on your instructions and
            only for the purposes the governing document permits; we do not
            repurpose regulated data for our own independent use.
          </p>
        </Article>

        <Article number="6" title="We Do Not Sell Your Data">
          <p>
            DBG does not sell Client Content or personal data, and does not
            share it with third parties for their own marketing. At present, we
            do not disclose Client Content to third parties except to the
            service providers and subprocessors described in Section 7, or as
            required by law.
          </p>
        </Article>

        <Article number="7" title="Service Providers, Subprocessors & Vendor Categories">
          <p style={{ marginBottom: 16 }}>
            We use a limited set of vendors to run the Services. These
            vendors process data only on our instructions and under
            contractual confidentiality and security obligations. A current
            list of named subprocessors is available on request, and where a
            DPA applies, subprocessor terms are governed by that DPA. The
            categories of vendors we use include:
          </p>
          <DataTable
            headers={["Category", "Purpose"]}
            rows={[
              [
                "Cloud hosting & infrastructure",
                "Hosting the Platform, storing data, and running Managed Services.",
              ],
              ["Analytics", "Understanding product usage and improving the Services."],
              [
                "CRM & support tooling",
                "Managing client communications, support tickets, and account records.",
              ],
              [
                "Payment processors",
                "Processing billing and payment information; we do not store full card numbers.",
              ],
              [
                "Communications platforms",
                "Email, video conferencing, and messaging used to deliver the Services and record sessions per Section 13.",
              ],
            ]}
          />
        </Article>

        <Article number="8" title="Protected Health Information & the HIPAA Boundary">
          <Callout label="Non-HIPAA Data and HIPAA Data Are Handled Separately — This Separation Is Important">
            <p>
              DBG draws a firm line between data that is never subject to
              HIPAA and data that is PHI governed only under a signed
              regulatory agreement. This Section is the safe-harbor boundary
              between the two.
            </p>
          </Callout>
          <DataTable
            headers={["Non-HIPAA Data", "HIPAA Data (PHI)"]}
            rows={[
              [
                "Website visitor activity and analytics",
                "Patient or member information transmitted under a signed BAA",
              ],
              [
                "Marketing inquiries and newsletter sign-ups",
                "Clinical, diagnosis, treatment, or claims data",
              ],
              [
                "Discovery-form and contact-form submissions",
                "Any data a Covered Entity or Business Associate designates as PHI",
              ],
              [
                "General sales and support communications",
                "Data covered by a Data Processing Addendum involving regulated health data",
              ],
            ]}
          />
          <p style={{ marginTop: 16, marginBottom: 16 }}>
            <strong style={{ color: PALETTE.white }}>
              8.1 Non-HIPAA Data.
            </strong>{" "}
            Website visitors, marketing inquiries, discovery-form submissions,
            and general communications are, by default, not PHI and are not
            governed by HIPAA. We handle this data under the general terms of
            this Policy — Sections 3, 4, and 7 — unless you separately submit
            PHI within it, in which case Section 8.2 applies to the PHI
            portion.
          </p>
          <p style={{ marginBottom: 12 }}>
            <strong style={{ color: PALETTE.white }}>8.2 HIPAA Data.</strong>
          </p>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              We handle PHI only under a Business Associate Agreement, and
              only for the purposes that agreement, the related DPA, and the
              governing MSA permit.
            </li>
            <li>
              PHI is excluded from the reserved operating license in Section
              4.1 and from the aggregated-data provisions in Section 4.2,
              except to the extent the BAA expressly permits a specific use.
            </li>
            <li>
              We do not use PHI for our own purposes, including product
              development, benchmarking, or marketing, beyond what the BAA
              permits.
            </li>
            <li>You must not transmit PHI to our systems before a BAA is executed.</li>
          </ul>
        </Article>

        <Article number="9" title="Artificial Intelligence & Automated Processing">
          <Callout label="AI Processing Notice">
            <p style={{ marginBottom: 12 }}>
              Some Services use artificial intelligence or automated systems
              to process data — for example, to generate content, surface
              insights, or automate a workflow. Where this applies:
            </p>
            <ul
              style={{
                paddingLeft: 20,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  data you submit may be processed by an AI system
                </strong>{" "}
                (operated by DBG or a contracted AI subprocessor) to produce
                the requested output;
              </li>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  AI processing of PHI or other regulated data
                </strong>{" "}
                occurs only where the governing BAA or DPA permits it, and
                only on the terms that agreement sets;
              </li>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  we do not use Client Content to train shared or
                  third-party AI models
                </strong>{" "}
                except where a signed agreement expressly permits it; and
              </li>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  AI Outputs may be inaccurate or incomplete
                </strong>{" "}
                — see Section 11.2 of the Platform Terms of Service for the
                human-review and no-reliance disclaimers that apply to AI
                Outputs.
              </li>
            </ul>
          </Callout>
        </Article>

        <Article number="10" title="Security">
          <p>
            We maintain administrative, technical, and physical safeguards
            designed to protect data appropriate to the nature of the Services.
            No system is perfectly secure; we cannot guarantee absolute
            security. Details of our current security program and the status of
            any third-party attestations are available on request.
          </p>
        </Article>

        <Article number="11" title="Data Retention Schedule">
          <p style={{ marginBottom: 16 }}>
            We retain data for as long as needed to provide the Services,
            meet legal and contractual obligations, resolve disputes, and
            enforce agreements. Typical retention periods by category are:
          </p>
          <DataTable
            headers={["Category", "Typical Retention"]}
            rows={[
              [
                "Leads & marketing inquiries",
                "Until you opt out or 24 months of inactivity, whichever is sooner.",
              ],
              [
                "Discovery-form submissions",
                "Duration of the sales process plus 12 months, unless converted to a Client record.",
              ],
              [
                "Recordings & transcripts",
                "Per the Terms and any applicable BAA/DPA; generally 12–24 months unless a longer period is required for a dispute, audit, or legal hold.",
              ],
              [
                "Client workspaces & Client Content",
                "Duration of the engagement, plus the export window stated in the Terms or your agreement after termination.",
              ],
              [
                "Backup & archive copies",
                "Rolling backup cycle of 30–90 days after the corresponding live data is deleted, after which backups are purged in the ordinary course.",
              ],
            ]}
          />
          <p style={{ marginTop: 16 }}>
            Aggregated and de-identified data may be retained indefinitely. On
            termination, you may export Client Content from DBG-managed
            systems within the window stated in the Terms or your agreement,
            after which we may delete it subject to legal retention
            requirements.
          </p>
        </Article>

        <Article number="12" title="Your Choices & Rights">
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              You may access, correct, or request export of your account data
              and Client Content as described in your agreement or by contacting
              us.
            </li>
            <li>
              Depending on your jurisdiction (for example, under California and
              other state privacy laws), you may have rights to access, delete,
              correct, or limit certain uses of personal data. We honor
              applicable rights and do not discriminate against you for
              exercising them. To make a request, contact us using the details
              below.
            </li>
          </ul>
        </Article>

        <Article number="13" title="Recordings">
          <p>
            As described in the Terms, we may record and retain communications,
            meetings, demonstrations, and presentations for quality, training,
            security, and recordkeeping. We provide notice before recording and
            handle recordings in accordance with this Policy and any applicable
            BAA or DPA. Recordings that capture regulated data are handled under
            the governing agreement and are excluded from the reserved
            operating license and aggregated-data provisions in Section 4.
          </p>
        </Article>

        <Article number="14" title="International & Cross-Border Processing">
          <p style={{ marginBottom: 16 }}>
            We operate primarily in the United States. Where you, your
            customers, or your data are located outside the United States,
            the following applies:
          </p>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              <strong style={{ color: PALETTE.white }}>U.S. clients.</strong>{" "}
              Data is processed and stored in the United States by default.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Canadian clients.
              </strong>{" "}
              Data may be processed in the United States. Where required, we
              will identify applicable cross-border transfer safeguards and
              any PIPEDA-related disclosures in your Order or DPA.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Other international engagements.
              </strong>{" "}
              As we take on clients outside the U.S. and Canada, we will
              identify the applicable transfer mechanism (such as standard
              contractual clauses or another recognized safeguard) and the
              jurisdiction-specific disclosures that law requires, and
              incorporate them into your DPA or this Policy before processing
              your data.
            </li>
          </ul>
        </Article>

        <Article number="15" title="Changes to This Policy">
          <p>
            We may update this Policy from time to time. Material changes will
            be reflected by updating the "Last Updated" date and, where
            appropriate, by notice. Continued use after the effective date
            constitutes acceptance.
          </p>
        </Article>

        <div
          style={{
            marginTop: 64,
            paddingTop: 32,
            borderTop: `1px solid ${PALETTE.border}`,
          }}>
          <p
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "10px",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: PALETTE.teal400,
              marginBottom: 14,
            }}>
            ✉ Contact
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "15px",
              lineHeight: 1.8,
              color: PALETTE.whiteMid,
            }}>
            Digital Billions Group LLC &nbsp;·&nbsp; &nbsp;·&nbsp;{" "}
            <Placeholder>legal@digitalbillionsgroup.com</Placeholder>
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
